import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	FormattedText,
	Grid,
	Heading,
	If,
	jsxEnglishCommaJoin,
	makeClassName,
	Modal,
	Row,
	Rule,
	Section,
	StyleProps,
	Tooltip,
	TooltipButton,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import { ViewRecorder } from "../../../../Components/ViewRecorder";
import { StudentMedicalEventDetailFragment } from "../../../../Utilities";
import { StudentMedicalEventDeleteButton } from "../../../DeleteButtons";
import { StudentFormat, StudentMedicalEventFormat, UserFormat } from "../../../Format";
import { EmployeeLink, StudentLink, UserLink } from "../../../Links";
import { MediaAttachmentUtilities } from "../../../Utilities";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import { StudentIcon } from "../../Student/Icon";
import { UserIcon } from "../../User";
import {
	StudentMedicalEventDeletePermissionRequired,
	StudentMedicalEventModifyPermissionRequired,
	StudentMedicalEventUpdatePermissionRequired,
} from "../PermissionRequired";
import { StudentMedicalEventUpdateModal } from "../UpdateModal";
import styles from "./styles.module.css";

export type DetailDisplayStudentMedicalEvent = StudentMedicalEventDetailFragment;

export type StudentMedicalEventDetailDisplayProps = {
	isLoading?: boolean;
	studentMedicalEvent: DetailDisplayStudentMedicalEvent | null | undefined;
};

export function StudentMedicalEventDetailDisplay({
	isLoading = false,
	studentMedicalEvent,
}: StudentMedicalEventDetailDisplayProps) {
	if (isLoading && !studentMedicalEvent) {
		return <div>Loading...</div>;
	}

	if (!studentMedicalEvent) {
		return null;
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1.5rem">
			<ViewRecorder id={studentMedicalEvent.id} />

			<Row justify="spaced-start" className={styles["detail-display__summary-information"]}>
				<Grid tracks={3} style={{ flexGrow: 1 }}>
					<LabeledItem label="Written by">
						<Conditional>
							<If condition={!!studentMedicalEvent.author}>
								{!!studentMedicalEvent.author && (
									<UserLink instance={studentMedicalEvent.author} className="link--no-text-decoration">
										<Row
											justify="spaced-start"
											align="center"
											horizontalSpacing="0.5rem"
											className={styles["detail-display__labeled-item__value--person"]}
										>
											<UserIcon user={studentMedicalEvent.author} size={16} />
											{UserFormat.name(studentMedicalEvent.author)}
										</Row>
									</UserLink>
								)}
							</If>
							<Else>
								<span>User Deleted</span>
							</Else>
						</Conditional>
					</LabeledItem>

					<LabeledItem label="Student">
						<Column justify="spaced-start" verticalSpacing="0.25rem">
							<StudentLink instance={studentMedicalEvent.student} className="link--no-text-decoration">
								<Row
									justify="spaced-start"
									align="center"
									horizontalSpacing="0.5rem"
									className={styles["detail-display__labeled-item__value--person"]}
								>
									<StudentIcon student={studentMedicalEvent.student} size={16} />
									{StudentFormat.name(studentMedicalEvent.student)}
								</Row>
							</StudentLink>
						</Column>
					</LabeledItem>

					<Column justify="spaced-start">
						<LabeledItem label="Check In Time">
							<span>{StudentMedicalEventFormat.Fields.checkInTime(studentMedicalEvent.checkInTime)}</span>
						</LabeledItem>
						<LabeledItem label="Check Out Time">
							<span>{StudentMedicalEventFormat.Fields.checkOutTime(studentMedicalEvent.checkOutTime)}</span>
						</LabeledItem>
					</Column>
				</Grid>

				<StudentMedicalEventModifyPermissionRequired>
					<MutationMenu studentMedicalEvent={studentMedicalEvent} />
				</StudentMedicalEventModifyPermissionRequired>
			</Row>

			<LabeledItem label="Description of medical incident or issue">
				<FormattedText>{studentMedicalEvent.description}</FormattedText>
			</LabeledItem>

			<LabeledItem label="Treatment provided to the student">
				<FormattedText>{studentMedicalEvent.treatment}</FormattedText>
			</LabeledItem>

			<LabeledItem label="Staff member(s) who provided treatment">
				{jsxEnglishCommaJoin(studentMedicalEvent.attendingEmployees.map((e) => <EmployeeLink instance={e} />))}
			</LabeledItem>

			<LabeledItem label="Outcome of treatment">
				<FormattedText>{studentMedicalEvent.outcome}</FormattedText>
			</LabeledItem>

			<LabeledItem label="Notes">
				<FormattedText>{studentMedicalEvent.notes}</FormattedText>
			</LabeledItem>

			<Rule thickness="2px" />

			<AttachmentsView studentMedicalEvent={studentMedicalEvent} />
		</Column>
	);
}

const updateFormStateArgs = {
	editMode: EditMode.WriteOnly,
};

type MutationMenuProps = {
	studentMedicalEvent: DetailDisplayStudentMedicalEvent;
};

function MutationMenu({ studentMedicalEvent }: MutationMenuProps) {
	const {
		isModalOpen: isUpdateModalOpen,
		openModal: openUpdateModal,
		closeModal: closeUpdateModal,
	} = Modal.useToggle();

	return (
		<React.Fragment>
			<TooltipButton side="bottom">
				{() => (
					<Tooltip isInteractable>
						<Tooltip.Body>
							<Column justify="spaced-start" align="center" verticalSpacing="0.25rem">
								<StudentMedicalEventUpdatePermissionRequired>
									<Button variant="link" size="small" onClick={openUpdateModal} style={{ width: "100%" }}>
										<Row justify="spaced-center" align="center" horizontalSpacing="0.25rem">
											<Icon.PencilSimple size={14} /> Edit
										</Row>
									</Button>
								</StudentMedicalEventUpdatePermissionRequired>

								<StudentMedicalEventDeletePermissionRequired>
									<StudentMedicalEventDeleteButton
										instanceID={studentMedicalEvent.id}
										size="small"
										style={{ width: "100%" }}
										buttonStyle={{ width: "100%" }}
									>
										<Row justify="spaced-center" align="center" horizontalSpacing="0.25rem">
											<Icon.Trash size={14} /> Delete
										</Row>
									</StudentMedicalEventDeleteButton>
								</StudentMedicalEventDeletePermissionRequired>
							</Column>
						</Tooltip.Body>
					</Tooltip>
				)}
				{({ onClick }) => (
					<Button variant="link" size="small" onClick={onClick} className="button--icon">
						<Icon.DotsThreeVertical size={16} className="block" />
					</Button>
				)}
			</TooltipButton>

			<Modal.If condition={isUpdateModalOpen}>
				<StudentMedicalEventUpdateModal
					studentMedicalEvent={studentMedicalEvent}
					onClose={closeUpdateModal}
					formStateArgs={updateFormStateArgs}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type LabeledItemProps = {
	label: string;
} & Partial<ClassNameProps & StyleProps & ChildrenProps>;

function LabeledItem({ label, className, style, children }: LabeledItemProps) {
	return (
		<Column
			justify="spaced-start"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["detail-display__labeled-item"], className)}
			style={style}
		>
			<span className={styles["detail-display__labeled-item__label"]}>{label}</span>
			{children}
		</Column>
	);
}

type AttachmentsViewProps = {
	studentMedicalEvent: DetailDisplayStudentMedicalEvent;
};

function AttachmentsView({ studentMedicalEvent }: AttachmentsViewProps) {
	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitMediaAttachments(studentMedicalEvent?.mediaAttachments),
		[studentMedicalEvent],
	);

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentComponents.MediaAttachmentModal.useModal();

	return (
		<Section>
			<Section.Header>
				<Heading.H4 noMargin>Attachments</Heading.H4>
			</Section.Header>
			<Section.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<If condition={visualAttachments.length > 0}>
						<MediaAttachmentComponents.VisualAttachmentsRow
							attachments={visualAttachments}
							onClickAttachment={onClickMediaAttachment}
							size="medium"
						/>

						<Modal.If condition={isMediaAttachmentModalOpen}>
							<MediaAttachmentComponents.MediaAttachmentModal
								mediaAttachments={visualAttachments}
								initialIndex={mediaAttachmentModalIndex}
								backButtonText="Back to Medical Event"
								onClose={closeMediaAttachmentModal}
							/>
						</Modal.If>
					</If>
					<If condition={fileAttachments.length > 0}>
						<MediaAttachmentComponents.FileAttachmentsRow attachments={fileAttachments} />
					</If>
					<If condition={linkAttachments.length > 0}>
						<MediaAttachmentComponents.LinkAttachmentsRow attachments={linkAttachments} />
					</If>
				</Column>
			</Section.Body>
		</Section>
	);
}
